import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { createRole, roleQueryKeys } from '@/features/roles/api';
import { RoleForm, type RoleFormValues } from '@/features/roles/RoleForm';
import type { CreateRole } from '@/features/roles/role';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from '@/lib/icons';
import { PageShell } from '@/components/layout/PageShell';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_layout/roles/new')({
  component: NewRolePage,
});

function NewRolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUser = useAppSelector((state) => state.auth.user);
  const canCreate = hasPermission(currentUser, 'users', 'create');

  const mutation = useMutation({
    mutationFn: (data: CreateRole) => createRole(data),
    onSuccess: (result) => {
      if (result.succeeded && result.value) {
        queryClient.invalidateQueries({ queryKey: roleQueryKeys.lists() });
        toast({
          title: t('common.success'),
          description: t('roles.create.success'),
        });
        navigate({
          to: '/roles/$roleId',
          params: { roleId: result.value.id },
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('common.error'),
          variant: 'destructive',
        });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('common.error'),
        variant: 'destructive',
      });
    },
  });

  if (!canCreate) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">{t('common.accessDenied')}</h1>
        <p className="text-muted-foreground">
          {t('common.noPermissionToView')}
        </p>
      </div>
    );
  }

  const onSubmit = async (values: RoleFormValues) => {
    await mutation.mutateAsync(values);
  };

  return (
    <PageShell
      title={t('roles.create.title')}
      description={t('roles.create.description')}
      actions={
        <Button variant="outline" onClick={() => navigate({ to: '/roles' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.actions.back')}
        </Button>
      }
    >
      <Card>
        <CardContent className="pt-6">
          <RoleForm
            submitLabel={t('common.actions.create')}
            submitting={mutation.isPending}
            onCancel={() => navigate({ to: '/roles' })}
            onSubmit={onSubmit}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
}
